/*
 * Interface strings, English.
 *
 * The "ui" bucket: everything outside the calculators. Every language the
 * picker offers has a full file here, so no English leaks into a translated
 * interface. Calculator text lives in i18n/calc, which is English and Spanish
 * only. Legal and disclaimer text is in neither: it stays English everywhere,
 * with legal.englishGoverning beside it.
 *
 * Plural keys end _one/_other and are chosen by Intl.PluralRules for this
 * language; Chinese, Thai, Vietnamese and Indonesian have one form, so they
 * carry only _other and every count lands there.
 */

const strings = {
  "landing.lead": "Sign in with your AquaNetica account to continue.",
  "landing.signIn": "Sign in",
  "landing.signUpFree": "Signing up is free.",
  "common.close": "Close",
  "common.cancel": "Cancel",
  "profile.title": "System profile · {name}",
  "profile.subtitle": "Gia uses this to calibrate every answer in this project. Leave fields blank if not relevant.",
  "profile.species": "Species",
  "profile.speciesPlaceholder": "e.g. Atlantic salmon",
  "profile.systemType": "System type",
  "profile.select": "Select…",
  "profile.sys.ras": "RAS (recirculating)",
  "profile.sys.flow": "Flow-through",
  "profile.sys.pond": "Pond",
  "profile.sys.cage": "Cage / sea pen",
  "profile.sys.hatchery": "Hatchery",
  "profile.sys.other": "Other",
  "profile.volume": "Total volume (L)",
  "profile.temp": "Target temp (°C)",
  "profile.lifeStage": "Life stage",
  "profile.lifeStagePlaceholder": "e.g. smolt, juvenile, broodstock",
  "profile.location": "Location",
  "profile.locationPlaceholder": "e.g. Tasmania, AU",
  "profile.notes": "Additional notes",
  "profile.notesPlaceholder": "Anything else Gia should know about this system",
  "profile.save": "Save profile",
  "help.title": "About & disclaimer",
  "help.about": "Gia is an AI assistant for aquaculture operators, with built-in engineering calculators. It answers from reference material and general knowledge to help you think through systems, water quality, and equipment.",
  "legal.englishGoverning": "The terms and disclaimer are provided in English. The English version is the governing version.",
  "feature.notNow": "Not now",
  "feature.seePlans": "See plans",
  "lang.title": "Language",
  "lang.sub": "Sets the interface, and the language Gia answers in whatever language you type in.",
  "lang.auto": "Auto (match my browser and question)",
  "pe.timeline.ASAP": "ASAP",
  "pe.timeline.1–3 months": "1–3 months",
  "pe.timeline.Just researching": "Just researching",
  "pe.condition.New": "New",
  "pe.condition.Used": "Used",
  "pe.condition.Either": "Either",
  "pe.missing.quantity": "how many you need",
  "pe.missing.city": "where it is going",
  "pe.missing.date": "when you need it",
  "pe.nudge": "Suppliers usually can't quote without knowing {list}. You can send it anyway.",
  "pe.err.name": "Please add your name so we know who to reply to.",
  "pe.err.email": "Please add your email so we can reply.",
  "pe.err.emailInvalid": "That email doesn't look right — please check it.",
  "pe.err.quantity": "Please add how many you need.",
  "pe.err.city": "Please add the delivery city.",
  "pe.err.send": "We couldn't send your request. Please try again.",
  "pe.err.network": "We couldn't reach the server. Please check your connection and try again.",
  "pe.title": "Help me source this",
  "pe.thanks": "Thanks — we’ll be in touch at {email}.",
  "pe.done": "Done",
  "pe.whatYouNeed": "What you need",
  "pe.equipment": "Equipment",
  "pe.suggestedModels": "Suggested models:",
  "pe.hideDetails": "Hide details",
  "pe.editDetails": "Edit details",
  "pe.item": "Item you need",
  "pe.specs": "Details and sizing",
  "pe.name": "Your name",
  "pe.email": "Email (so we can reply)",
  "pe.when": "When do you need it?",
  "pe.quantity": "How many do you need?",
  "pe.quantityPlaceholder": "e.g. 2 units",
  "pe.city": "Delivery city",
  "pe.cityPlaceholder": "Where should it be delivered?",
  "pe.neededBy": "Needed by (optional)",
  "pe.condition": "New or used? (optional)",
  "pe.budget": "Rough budget (optional)",
  "pe.amount": "Amount",
  "pe.budgetAmount": "Budget amount",
  "pe.budgetCurrency": "Budget currency",
  "pe.notSure": "Not sure",
  "pe.note": "Anything else? (optional)",
  "pe.notePlaceholder": "Anything else that helps us find the right thing.",
  "pe.hideFarm": "Hide farm details",
  "pe.addFarm": "Add farm details (optional)",
  "pe.farm": "Farm or company name",
  "pe.country": "Country",
  "pe.sending": "Sending…",
  "pe.send": "Send request",
  "pe.reassurance": "No obligation. A real person from AquaNetica will reply by email.",
  "toolbar.answerLanguage": "Language: {name}",
  "toolbar.answerLanguageTitle": "Language",
  "project.namePrompt": "Project name?",
  "project.renamePrompt": "Rename project:",
  "project.deleteWithChats": "Delete project \"{name}\" and its {count} chat?",
  "project.deleteWithChats_one": "Delete project \"{name}\" and its {count} chat?",
  "project.deleteWithChats_other": "Delete project \"{name}\" and its {count} chats?",
  "project.delete": "Delete project \"{name}\"?",
  "file.proTitle": "File messages are a Pro feature",
  "file.proMessage": "Your plan doesn’t include file messages. Upgrade to Pro to attach spec sheets, drawings and photos for Gia to read. A file message is any message you send while a file is attached.",
  "file.badType": "Please attach an image (JPG, PNG, GIF, WEBP), a PDF, a Word, Excel or PowerPoint file (.docx, .xlsx, .pptx), or a text or code file (.txt, .md, .csv, .json, .r, .py).",
  "file.tooLarge": "File is too large. Please keep attachments under 20 MB.",
  "file.unreadable": "Gia couldn’t read that file. If it’s password-protected, or an older .doc, .xls or .ppt file, save it as .docx, .xlsx or .pptx without a password and attach it again.",
  "file.noText": "That file has no text Gia can read. If it’s a scan or mostly pictures, attach it as a PDF or an image instead.",
  "file.tooBig": "That file is too big for Gia to read in one go. Split it into smaller files, or attach just the part you need.",
  "file.notText": "That file isn’t plain text, even though its name says it is. Save it as text (UTF-8) and attach it again.",
  "chat.errSomething": "Sorry, something went wrong: {detail}",
  "chat.errNoBackend": "Sorry, I couldn't reach the knowledge base. Check the backend is running on port 3001.",
  "file.attached": "· attached to this chat",
  "file.left": "{count} file message left this month",
  "file.left_one": "{count} file message left this month",
  "file.left_other": "{count} file messages left this month",
  "file.detachTitle": "Detach file — stops it counting toward your file messages",
  "file.detachLabel": "Detach file",
  "file.detach": "Detach",
  "file.explain": "Each message you send while this is attached counts as one file message. Detach it when you no longer need Gia to reference it.",
  "file.reattachHint": "{name} — re-attach to keep asking about it",
  "file.reattachTitle": "Re-attach file",
  "file.reattach": "Re-attach",
  "usage.questions": "{left} of {limit} question left this month",
  "usage.questions_one": "{left} of {limit} question left this month",
  "usage.questions_other": "{left} of {limit} questions left this month",
  "usage.questionsShort": "{left} of {limit} question left",
  "usage.questionsShort_one": "{left} of {limit} question left",
  "usage.questionsShort_other": "{left} of {limit} questions left",
  "usage.files": "{left} of {limit} file message",
  "usage.files_one": "{left} of {limit} file message",
  "usage.files_other": "{left} of {limit} file messages",
  "usage.leftShort": "{left} question left",
  "usage.leftShort_one": "{left} question left",
  "usage.leftShort_other": "{left} questions left",
  "usage.remaining": "{left} / {limit} question remaining",
  "usage.remaining_one": "{left} / {limit} question remaining",
  "usage.remaining_other": "{left} / {limit} questions remaining",
  "usage.upgrade": "Upgrade",
  "wall.daily": "You've reached today's message limit",
  "wall.feature": "{plan} plan doesn't include this",
  "wall.featureYour": "Your plan doesn't include this",
  "wall.files": "You've used all {limit} of your {plan} file messages this month",
  "wall.questions": "You've used all {limit} of your {plan} questions this month",
  "wall.generic": "You've reached a limit on your plan",
  "wall.region": "Question limit reached",
  "wall.refresh": "Your {plan} questions refresh on the 1st.",
  "wall.refreshNoPlan": "Your questions refresh on the 1st.",
  "wall.stayFree": "The calculators stay free and unlimited, and all your past chats remain here to read.",
  "wall.upgrade": "Upgrade at aquanetica.com",
  "sidebar.hide": "Hide sidebar",
  "sidebar.search": "Search chats…",
  "sidebar.newChat": "+ New chat",
  "sidebar.newProject": "+ Project",
  "sidebar.tools": "🛠️ Tools",
  "sidebar.backToGia": "Back to Gia",
  "sidebar.calculator": "Calculator",
  "sidebar.empty": "No chats yet. Ask Gia anything to start.",
  "sidebar.profileSet": "System profile set",
  "sidebar.newInProject": "New chat in this project",
  "sidebar.editProfile": "Edit profile",
  "sidebar.rename": "Rename",
  "sidebar.delete": "Delete",
  "sidebar.projectEmpty": "No chats yet",
  "sidebar.noMatches": "No matches for \"{q}\"",
  "sidebar.signedIn": "Signed in",
  "sidebar.signOut": "Sign out",
  "sidebar.signingOut": "Signing out…",
  "signout.title": "Sign out",
  "signout.body": "You will be signed out of AquaNetica IQ and of aquanetica.com. Uploaded documents and message ratings are always removed from this device. Everything else you have saved stays here for when you sign back in.",
  "signout.alsoClear": "Also delete my saved work on this device: conversations, projects and calculator work",
  "signout.irreversible": "This extra deletion is permanent. That work is stored only in this browser, so there is no copy to restore.",
  "signout.confirm": "Sign out",
  "sidebar.communityTitle": "Back to the AquaNetica community (opens in a new tab)",
  "sidebar.community": "Back to AquaNetica",
  "sidebar.help": "Help and disclaimer",
  "drop.label": "Drop file to attach",
  "toolbar.showSidebar": "Show sidebar",
  "toolbar.toggleSidebar": "Toggle sidebar",
  "toolbar.newChat": "New chat",
  "toolbar.inProject": "In project:",
  "toolbar.home": "Back to AquaNetica community",
  "toolbar.homeTitle": "Back to AquaNetica (opens in a new tab)",
  "toolbar.calc": "Open calculator",
  "toolbar.theme": "Toggle theme",
  "hero.title": "What can I help you with?",
  "hero.calibrated": "Gia is calibrated to this project's system profile.",
  "hero.noProfile": "No system profile yet. Click ⚙ on the project to set one.",
  "hero.subtitle": "Ask anything about aquaculture systems, water quality, or equipment.",
  "composer.attachTitle": "Attach an image or PDF",
  "composer.attachLabel": "Attach a file",
  "composer.send": "Send",
  "answer.copyTitle": "Copy answer",
  "answer.copied": "Copied",
  "answer.copy": "Copy",
  "answer.retryTitle": "Ask this question again",
  "answer.retryLabel": "Retry answer",
  "answer.retry": "Retry",
  "answer.good": "Good answer",
  "answer.bad": "Bad answer",
  "answer.sourcing": "Need help sourcing this? →",
  "answer.searching": "Searching the document library…",
  "answer.scrollLatest": "Scroll to latest",
  "sidebar.otherChats": "Other",
  "trial.have": "Have a code?",
  "trial.title": "Trial code",
  "trial.placeholder": "Enter your trial code",
  "trial.submit": "Redeem",
  "trial.checking": "Checking…",
  "trial.invalid": "That code isn't valid. Check it and try again.",
  "trial.error": "We couldn't check that code. Please try again.",
  "trial.success": "Pro trial started. You have 75 questions and 10 file messages a month until {date}.",
  "trial.active": "Pro trial until {date}",
  "sidebar.planTrial": "{plan} trial",
  "sidebar.managePlan": "Manage plan",
  "trial.ends": "Trial ends {date}",
  "pe.consent": "I agree to my enquiry details being shared with AquaNetica and with suppliers approached on my behalf, and to the {terms} and {privacy}.",
  "pe.consentTerms": "Terms of Service",
  "pe.consentPrivacy": "Privacy Policy",
};

export default strings;
